import { useTranslation } from 'react-i18next'
import { RemoveIcon } from './icons'
import type { FormState, Slot } from './types'

interface WeeklyScheduleProps {
	form: FormState
	addSlot: (day: number) => void
	removeSlot: (idx: number) => void
	updateSlot: (idx: number, field: keyof Slot, value: string | number) => void
}

export function WeeklySchedule({ form, addSlot, removeSlot, updateSlot }: WeeklyScheduleProps) {
	const { t } = useTranslation('common')

	const dayNames = [
		t('account.availability.days.mon'),
		t('account.availability.days.tue'),
		t('account.availability.days.wed'),
		t('account.availability.days.thu'),
		t('account.availability.days.fri'),
		t('account.availability.days.sat'),
		t('account.availability.days.sun'),
	]

	return (
		<section className="flex flex-col gap-3">
			<h2 className="text-sm font-medium text-grey-700 uppercase tracking-wide">
				{t('account.availability.weekly_schedule.title')}
			</h2>
			<p className="text-xs text-grey-400">
				{t('account.availability.weekly_schedule.description')}
			</p>
			<div className="flex flex-col gap-2 divide-y divide-gray-50">
				{[1, 2, 3, 4, 5, 6, 7].map((day) => {
					const daySlots = form.schedule.slots
						.map((slot, idx) => ({ slot, idx }))
						.filter(({ slot }) => slot.day === day)

					return (
						<div key={day} className="flex items-start gap-4 py-2">
							<div className="w-12 shrink-0 text-sm font-medium text-grey-600">
								{dayNames[day - 1]}
							</div>
							<div className="flex-1 flex flex-col gap-2">
								{daySlots.map(({ slot, idx }) => (
									<div key={idx} className="flex items-center gap-2">
										<input
											type="time"
											value={slot.start}
											onChange={(e) => updateSlot(idx, 'start', e.target.value)}
											className="rounded border border-grey-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
										/>
										<span className="text-sm text-grey-400">–</span>
										<input
											type="time"
											value={slot.end}
											onChange={(e) => updateSlot(idx, 'end', e.target.value)}
											className="rounded border border-grey-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
										/>
										<button
											type="button"
											onClick={() => removeSlot(idx)}
											className="p-1 text-grey-400 hover:text-red-500 transition-colors"
											aria-label="Remove slot"
										>
											<RemoveIcon />
										</button>
									</div>
								))}
								<button
									type="button"
									onClick={() => addSlot(day)}
									className="shrink-0 self-start inline-block text-xs text-brand-600 hover:text-brand-700 font-medium"
								>
									{t('account.availability.weekly_schedule.add_time')}
								</button>
							</div>
						</div>
					)
				})}
			</div>
		</section>
	)
}
